import React, { useState } from "react";

// ─── GOOGLE ICON SVG ──────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ display: "block" }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

// ─── FULL AUTH MODAL ──────────────────────────────────────────
export function AuthModal({ onSignIn, onClose, loading }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box auth-modal-box" onClick={e => e.stopPropagation()}>
        <div className="auth-modal-icon">☁️</div>
        <h2 style={{ margin: "8px 0 6px", textAlign: "center" }}>Save Your Progress</h2>
        <p className="auth-modal-desc">
          Sign in with Google to protect your collection across devices.
          Your hamsters, coins, achievements and prestige will be saved automatically.
        </p>

        <div className="auth-perks">
          <div className="auth-perk">🐹 Collection backed up to the cloud</div>
          <div className="auth-perk">🏆 Leaderboard rank tied to your account</div>
          <div className="auth-perk">📱 Play on any device, pick up where you left off</div>
          <div className="auth-perk">☁️ Auto-saves every 60 seconds</div>
        </div>

        <button
          className="auth-google-btn"
          onClick={onSignIn}
          disabled={loading}
        >
          {loading ? (
            <span className="auth-btn-loading">Signing in…</span>
          ) : (
            <>
              <GoogleIcon />
              <span>Continue with Google</span>
            </>
          )}
        </button>

        <button className="auth-guest-btn" onClick={onClose}>
          Continue as Guest
        </button>

        <p className="auth-privacy-note">
          We only use your Google account for sign-in. No data is shared with third parties.
        </p>
      </div>
    </div>
  );
}

// ─── CONFLICT RESOLUTION MODAL ────────────────────────────────
export function ConflictModal({ conflictData, onChoose }) {
  const fmt = (state) => {
    const rolls    = state.rollCount  || 0;
    const inv      = (state.inventory || []).length;
    const prestige = state.prestige   || 0;
    const date     = state.savedAt
      ? new Date(state.savedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : "Unknown date";
    return { rolls, inv, prestige, date };
  };

  const local = fmt(conflictData.local);
  const cloud = fmt(conflictData.cloud);

  return (
    <div className="modal-overlay">
      <div className="modal-box auth-modal-box" onClick={e => e.stopPropagation()}>
        <div className="auth-modal-icon">⚠️</div>
        <h2 style={{ margin: "8px 0 6px", textAlign: "center" }}>Two Saves Found</h2>
        <p className="auth-modal-desc">
          We found progress on this device and in the cloud. Which do you want to keep?
        </p>

        <div className="conflict-options">
          <div className="conflict-option">
            <div className="conflict-option-label">📱 This Device</div>
            <div className="conflict-stat">{local.rolls.toLocaleString()} rolls</div>
            <div className="conflict-stat">{local.inv} hamsters</div>
            {local.prestige > 0 && <div className="conflict-stat">⭐ Prestige {local.prestige}</div>}
            <div className="conflict-date">Last played: {local.date}</div>
            <button className="conflict-choose-btn" onClick={() => onChoose("local")}>
              Use This Device's Save
            </button>
          </div>

          <div className="conflict-option conflict-option-cloud">
            <div className="conflict-option-label">☁️ Cloud Save</div>
            <div className="conflict-stat">{cloud.rolls.toLocaleString()} rolls</div>
            <div className="conflict-stat">{cloud.inv} hamsters</div>
            {cloud.prestige > 0 && <div className="conflict-stat">⭐ Prestige {cloud.prestige}</div>}
            <div className="conflict-date">Last saved: {cloud.date}</div>
            <button className="conflict-choose-btn conflict-choose-cloud" onClick={() => onChoose("cloud")}>
              Use Cloud Save
            </button>
          </div>
        </div>

        <p className="auth-privacy-note" style={{ marginTop: 12 }}>
          The save you don't pick will be permanently overwritten.
        </p>
      </div>
    </div>
  );
}

// ─── SAVE PROMPT BANNER (shown to guests after 20 rolls) ──────
export default function SavePrompt({ rollCount, onSignIn, loading }) {
  const [dismissed, setDismissed] = useState(
    () => !!sessionStorage.getItem("savePromptDismissed")
  );

  if (dismissed || rollCount < 20) return null;

  return (
    <div className="save-prompt-banner">
      <div className="save-prompt-left">
        <span className="save-prompt-icon">☁️</span>
        <div>
          <div className="save-prompt-title">Your progress isn't saved</div>
          <div className="save-prompt-sub">Sign in to protect your {rollCount} rolls</div>
        </div>
      </div>
      <div className="save-prompt-actions">
        <button className="save-prompt-signin" onClick={onSignIn} disabled={loading}>
          {loading ? "…" : "Save Now"}
        </button>
        <button className="save-prompt-dismiss" onClick={() => {
          sessionStorage.setItem("savePromptDismissed", "1");
          setDismissed(true);
        }}>✕</button>
      </div>
    </div>
  );
}